import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  User,
  AlertTriangle,
  CheckCircle,
  Plus,
  Edit2,
  Trash2,
  Bell,
  MessageSquare,
  Settings,
  Star,
  Target,
  Phone,
  CreditCard,
  Wrench,
  Award,
  RotateCcw
} from 'lucide-react';
import { format, formatDistanceToNow, isAfter, isBefore, addDays } from 'date-fns';
import { 
  useFollowUps, 
  useCreateFollowUp, 
  useUpdateFollowUp, 
  useCompleteFollowUp, 
  useDeleteFollowUp 
} from '@/hooks/useBookings';
import type { FollowUpEntry } from '@/lib/api/bookingService';

// FollowUpEntry interface now imported from bookingService
// Renaming to maintain compatibility with existing component code
type FollowUp = FollowUpEntry & {
  completed_date?: string; // additional field for compatibility
  assigned_to_name?: string; // additional field for compatibility
};

interface BookingFollowUpsProps {
  bookingId: number;
  userRole: 'customer' | 'provider' | 'admin';
  canManageFollowUps?: boolean;
  className?: string;
  compact?: boolean;
}

const BookingFollowUps: React.FC<BookingFollowUpsProps> = ({
  bookingId,
  userRole,
  canManageFollowUps = false,
  className = '',
  compact = false
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState<number | null>(null);
  const [completingFollowUp, setCompletingFollowUp] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Use hooks for follow-ups data and mutations
  const { 
    data: followUpsData, 
    isLoading: loading, 
    error: followUpsError,
    refetch: refetchFollowUps
  } = useFollowUps(bookingId);
  
  const createFollowUpMutation = useCreateFollowUp();
  const updateFollowUpMutation = useUpdateFollowUp();
  const completeFollowUpMutation = useCompleteFollowUp();
  const deleteFollowUpMutation = useDeleteFollowUp();
  
  // Transform follow-ups data to match existing component expectations
  const followUps: FollowUp[] = (followUpsData || []).map(followUp => ({
    ...followUp,
    completed_date: followUp.completed_at,
    assigned_to_name: `User ${followUp.assigned_to}` // placeholder for now
  }));

  // Form state
  const [formData, setFormData] = useState({
    follow_up_type: 'reminder' as const,
    title: '',
    description: '',
    due_date: '',
    priority: 'medium' as const,
    assigned_to: '',
    assigned_role: '' as const
  });

  // Completion form state
  const [completionData, setCompletionData] = useState({
    completion_notes: ''
  });

  // Handle follow-ups error states
  React.useEffect(() => {
    if (followUpsError && !followUpsError.message?.includes('404')) {
      setError('Failed to load follow-ups');
    } else {
      setError(null);
    }
  }, [followUpsError]);

  const handleCreateFollowUp = async () => {
    try {
      const payload = {
        ...formData,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : undefined,
        assigned_role: formData.assigned_role || undefined
      };

      await createFollowUpMutation.mutateAsync({
        bookingId: typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId,
        followUp: payload
      });
      
      setShowAddForm(false);
      resetForm();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to create follow-up');
      console.error('Create follow-up error:', err);
    }
  };

  const handleUpdateFollowUp = async (followUpId: number) => {
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        due_date: formData.due_date,
        priority: formData.priority
      };

      await updateFollowUpMutation.mutateAsync({
        bookingId: typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId,
        followUpId,
        followUp: payload
      });
      
      setEditingFollowUp(null);
      resetForm();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update follow-up');
      console.error('Update follow-up error:', err);
    }
  };

  const handleCompleteFollowUp = async (followUpId: number) => {
    try {
      await completeFollowUpMutation.mutateAsync({
        bookingId: typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId,
        followUpId,
        completionData
      });
      
      setCompletingFollowUp(null);
      setCompletionData({ completion_notes: '' });
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to complete follow-up');
      console.error('Complete follow-up error:', err);
    }
  };

  const handleDeleteFollowUp = async (followUpId: number) => {
    if (!confirm('Are you sure you want to delete this follow-up?')) return;

    try {
      await deleteFollowUpMutation.mutateAsync({
        bookingId: typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId,
        followUpId
      });
      
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete follow-up');
      console.error('Delete follow-up error:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      follow_up_type: 'reminder',
      title: '',
      description: '',
      due_date: '',
      priority: 'medium',
      assigned_to: '',
      assigned_role: '' as const
    });
  };

  const startEditing = (followUp: FollowUp) => {
    setFormData({
      follow_up_type: followUp.follow_up_type,
      title: followUp.title,
      description: followUp.description,
      due_date: format(new Date(followUp.due_date), "yyyy-MM-dd'T'HH:mm"),
      priority: followUp.priority,
      assigned_to: followUp.assigned_to?.toString() || '',
      assigned_role: followUp.assigned_role || '' as const
    });
    setEditingFollowUp(followUp.follow_up_id);
    setShowAddForm(true);
  };

  const getFollowUpTypeIcon = (type: string) => {
    const iconClass = compact ? 'h-4 w-4' : 'h-5 w-5';
    
    switch (type) {
      case 'reminder':
        return <Bell className={iconClass} />;
      case 'inspection':
        return <Settings className={iconClass} />;
      case 'payment':
        return <CreditCard className={iconClass} />;
      case 'feedback':
        return <MessageSquare className={iconClass} />;
      case 'warranty':
        return <Award className={iconClass} />;
      case 'maintenance':
        return <Wrench className={iconClass} />;
      case 'callback':
        return <Phone className={iconClass} />;
      default:
        return <Calendar className={iconClass} />;
    }
  };

  const getStatusColor = (status: string, dueDate: string) => {
    const isOverdue = isAfter(new Date(), new Date(dueDate)) && 
                     !['completed', 'cancelled'].includes(status);
    
    if (isOverdue) return 'text-red-600 bg-red-50 border-red-200';
    
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'scheduled':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'pending':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-700 bg-red-100';
      case 'high':
        return 'text-orange-700 bg-orange-100';
      case 'medium':
        return 'text-blue-700 bg-blue-100';
      case 'low':
        return 'text-gray-700 bg-gray-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getDueDateStatus = (dueDate: string, status: string) => {
    if (['completed', 'cancelled'].includes(status)) return 'completed';
    
    const due = new Date(dueDate);
    const now = new Date();
    const tomorrow = addDays(now, 1);
    
    if (isAfter(now, due)) return 'overdue';
    if (isBefore(due, tomorrow)) return 'due-soon';
    return 'upcoming';
  };

  if (loading) {
    return (
      <div className={`animate-pulse space-y-4 ${className}`}>
        {[...Array(2)].map((_, i) => (
          <div key={i} className="p-4 border border-gray-200 rounded-lg">
            <div className="flex space-x-4">
              <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => refetchFollowUps()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className={`font-semibold text-gray-900 ${compact ? 'text-lg' : 'text-xl'}`}>
          Follow-ups
        </h3>
        {canManageFollowUps && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={`inline-flex items-center border border-transparent font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              compact ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm'
            }`}
          >
            <Plus className={`mr-2 ${compact ? 'h-3 w-3' : 'h-4 w-4'}`} />
            Add Follow-up
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h4 className={`font-medium text-gray-900 mb-4 ${compact ? 'text-base' : 'text-lg'}`}>
            {editingFollowUp ? 'Edit Follow-up' : 'Add Follow-up'}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.follow_up_type}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  follow_up_type: e.target.value as any 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="reminder">Reminder</option>
                <option value="inspection">Inspection</option>
                <option value="payment">Payment</option>
                <option value="feedback">Feedback</option>
                <option value="warranty">Warranty</option>
                <option value="maintenance">Maintenance</option>
                <option value="callback">Callback</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  priority: e.target.value as any 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter follow-up title..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date & Time
              </label>
              <input
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter description..."
              />
            </div>
          </div>

          <div className="mt-6 flex space-x-3">
            <button
              onClick={editingFollowUp ? () => handleUpdateFollowUp(editingFollowUp) : handleCreateFollowUp}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {editingFollowUp ? 'Update Follow-up' : 'Create Follow-up'}
            </button>
            
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingFollowUp(null);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Follow-ups List */}
      <div className="space-y-4">
        {followUps.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No follow-ups scheduled</p>
          </div>
        ) : (
          followUps.map((followUp) => (
            <div
              key={followUp.follow_up_id}
              className={`p-4 border rounded-lg ${
                getDueDateStatus(followUp.due_date, followUp.status) === 'overdue' 
                  ? 'border-red-200 bg-red-50' 
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex space-x-4 flex-1">
                  {/* Icon */}
                  <div className={`flex-shrink-0 flex items-center justify-center rounded-full p-2 ${
                    getPriorityColor(followUp.priority)
                  }`}>
                    {getFollowUpTypeIcon(followUp.follow_up_type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className={`font-medium text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>
                        {followUp.title}
                      </h4>
                      
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                        getStatusColor(followUp.status, followUp.due_date)
                      }`}>
                        {followUp.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                        {getDueDateStatus(followUp.due_date, followUp.status) === 'overdue' && 
                         followUp.status !== 'completed' && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {followUp.status.replace('_', ' ')}
                      </span>

                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        getPriorityColor(followUp.priority)
                      }`}>
                        {followUp.priority}
                      </span>
                    </div>

                    <p className={`text-gray-600 mb-3 ${compact ? 'text-xs' : 'text-sm'}`}>
                      {followUp.description}
                    </p>

                    <div className={`flex items-center space-x-4 ${
                      compact ? 'text-xs' : 'text-sm'
                    } text-gray-500`}>
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        Due: {format(new Date(followUp.due_date), 'MMM dd, yyyy HH:mm')}
                      </span>
                      
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {formatDistanceToNow(new Date(followUp.due_date), { addSuffix: true })}
                      </span>

                      {followUp.assigned_to_name && (
                        <span className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          {followUp.assigned_to_name}
                        </span>
                      )}
                    </div>

                    {followUp.completion_notes && (
                      <div className="mt-3 p-2 bg-green-50 border-l-4 border-green-200">
                        <p className={`text-green-700 ${compact ? 'text-xs' : 'text-sm'}`}>
                          <strong>Completion Notes:</strong> {followUp.completion_notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {canManageFollowUps && (
                  <div className="flex space-x-1 ml-4">
                    {followUp.status !== 'completed' && (
                      <button
                        onClick={() => setCompletingFollowUp(followUp.follow_up_id)}
                        className="p-1 text-gray-400 hover:text-green-600 rounded"
                        title="Complete follow-up"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => startEditing(followUp)}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      title="Edit follow-up"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteFollowUp(followUp.follow_up_id)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                      title="Delete follow-up"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Completion Form */}
              {completingFollowUp === followUp.follow_up_id && (
                <div className="mt-4 p-3 bg-gray-50 rounded border">
                  <h5 className="font-medium text-gray-900 mb-3">Complete Follow-up</h5>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Completion Notes
                    </label>
                    <textarea
                      value={completionData.completion_notes}
                      onChange={(e) => setCompletionData({ completion_notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe what was completed..."
                    />
                  </div>

                  <div className="flex space-x-3 mt-4">
                    <button
                      onClick={() => handleCompleteFollowUp(followUp.follow_up_id)}
                      className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                    >
                      Complete
                    </button>
                    
                    <button
                      onClick={() => {
                        setCompletingFollowUp(null);
                        setCompletionData({ completion_notes: '' });
                      }}
                      className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BookingFollowUps;